import { useEffect, useState } from "react";
import SubscriptionsTab from "./SubscriptionsTab";
import { SubTabs, Card, KV, Pill, Loading, ErrorState, StatCard, ExportButton } from "./AdminUI";
import { api } from "@/lib/api";

const TABS = [
  { key: "subscriptions", label: "Subscriptions" },
  { key: "finance", label: "Finance" },
];

export default function BillingTab({ initialTab }) {
  const [tab, setTab] = useState(initialTab === "finance" ? "finance" : "subscriptions");
  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);
  return (
    <div data-testid="admin-billing-tab">
      <SubTabs tabs={TABS} active={tab} onChange={setTab} testidPrefix="billing-tab" />
      {tab === "subscriptions" ? <SubscriptionsTab /> : <FinancePanel />}
    </div>
  );
}

const money = c => (c == null ? "—" : `$${(c / 100).toFixed(2)}`);

function FinancePanel() {
  const [f, setF] = useState(null);
  const [health, setHealth] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/admin/finance").then(r => setF(r.data)).catch(e => setError(e.response?.data?.detail || e.message));
    api.get("/admin/health").then(r => setHealth(r.data)).catch(() => {});
  }, []);

  if (error) return <ErrorState error={error} />;
  if (!f) return <Loading />;
  const live = f.stripe_mode === "live";

  return (
    <div className="space-y-5" data-testid="admin-finance-panel">
      <div className={`rounded-2xl px-4 py-3 border ${live ? "bg-white border-black/10" : "bg-amber-50 border-amber-200"}`}
        data-testid="finance-mode-banner">
        <div className="flex items-center gap-2 flex-wrap">
          <Pill tone={live ? "green" : "amber"}>Stripe: {live ? "PRODUCTION" : "TEST / SANDBOX"}</Pill>
          <Pill tone="neutral">Enrollment paused</Pill>
        </div>
        <p className="text-xs text-[#222]/65 mt-2">
          {live
            ? "Live Stripe keys are active. All figures below are real money."
            : "Stripe is in test mode. Sandbox transactions are flagged livemode=false and are excluded from every revenue figure — test and production data are never mixed."}
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <StatCard testid="finance-collected" label="Collected revenue"
          value={money(f.actual_collected_revenue_cents.value)}
          sub={`${f.actual_collected_revenue_cents.excluded_sandbox_transactions} sandbox rows excluded`} />
        <StatCard testid="finance-mrr" label="Estimated MRR"
          value={f.estimated_mrr.available ? `$${f.estimated_mrr.value}` : "—"}
          sub={f.estimated_mrr.formula} />
        <StatCard testid="finance-active" label="Stripe-backed subscribers"
          value={f.active_subscription_value.real_stripe_backed_subscribers}
          sub={`${f.active_subscription_value.mocked_or_manual_pro_plus_accounts} manual/QA excluded`} />
        <StatCard testid="finance-past-due" label="Past due" value={f.past_due.value}
          tone={f.past_due.value ? "alert" : undefined} sub={`${f.canceled.value} canceled`} />
      </div>

      <Card title="Payment exceptions" testid="finance-exceptions"
        right={<ExportButton kind="finance" label="Finance reconciliation" />}>
        <KV label="Failed / unpaid transaction records" value={f.failed_payments.value} />
        <KV label="Refunds" value={f.refunded.available ? f.refunded.value : "Not tracked"} />
        <KV label="Stripe webhook events processed" value={health?.stripe?.webhook_events_processed ?? "—"} />
        <KV label="Unresolved webhook failures" value={health?.stripe?.webhook_failures_unresolved ?? "—"} />
        <KV label="Deletion-related billing failures"
          value={health?.alerts?.filter(a => a.kind === "account_deletion_stuck").length ?? 0} />
        <p className="text-[11px] text-[#222]/45 mt-3">{f.refunded.note}</p>
      </Card>

      <Card title="How each figure is derived" testid="finance-sources">
        <KV label="Collected revenue" value={f.actual_collected_revenue_cents.formula} />
        <KV label="Sandbox handling" value={f.actual_collected_revenue_cents.sandbox_note} />
        <KV label="Pro Plus price (live from Stripe)" value={f.pro_plus_pricing?.display || "unavailable"} />
      </Card>
    </div>
  );
}
